/**
 * Type definitions for the LittleBooker data model
 */

// Account types for multi-tenancy
export type AccountType = 'admin' | 'tenant' | 'dev';
export type AccountMemberRole = 'owner' | 'admin' | 'member' | 'viewer';

// User types for multi-user access within an account
export type UserRole = 'owner' | 'admin';

export interface User {
  id: string;
  account_id: string;
  email: string;
  name: string;
  role: UserRole;
  is_active: boolean;
  last_login_at: string | null;
  created_by: string | null;
  invite_token: string | null;
  invited_at: string | null;
  invited_by: string | null;
  accepted_at: string | null;
  created_at: string;
  updated_at: string;
}

export type UserInput = Omit<User, 'id' | 'created_at' | 'updated_at' | 'last_login_at'>;

// User creation with password (normal flow)
export type UserCreate = Pick<UserInput, 'email' | 'name'> & {
  password: string; // Plain password, will be hashed
  role?: UserRole;
};

// User creation with invite (password-less flow)
export type UserInvite = Pick<UserInput, 'email' | 'name'> & {
  role?: UserRole;
  // invite_token and invited_at will be generated
};

// Accept invite flow
export type UserInviteAccept = {
  password: string; // Set password when accepting invite
};

export type UserUpdate = Partial<Pick<UserInput, 'name' | 'role' | 'is_active'>> & {
  password?: string; // Optional password change
  accepted_at?: string | null; // Mark invite as accepted
};

export interface UserSession {
  user_id: string;
  user_email: string;
  user_name: string;
  user_role: UserRole;
  account_id: string;
  account_slug: string;
  account_name: string;
  account_type: AccountType;
}

export interface Account {
  id: string;
  slug: string;
  name: string;
  type: AccountType;
  settings: Record<string, unknown>;
  storage_bucket_prefix: string | null;
  timezone: string; // IANA timezone identifier (e.g., "Europe/Amsterdam", "UTC")
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AccountMember {
  id: string;
  account_id: string;
  user_id: string;
  role: AccountMemberRole;
  invited_by: string | null;
  invited_at: string | null;
  accepted_at: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export type AccountInput = Omit<Account, 'id' | 'created_at' | 'updated_at'>;
export type AccountCreate = Partial<AccountInput> & Pick<AccountInput, 'slug' | 'name'>;
export type AccountUpdate = Partial<Omit<AccountInput, 'slug'>>;

export type AccountMemberInput = Omit<AccountMember, 'id' | 'created_at' | 'updated_at'>;
export type AccountMemberCreate = Pick<AccountMemberInput, 'account_id' | 'user_id'> & Partial<Omit<AccountMemberInput, 'account_id' | 'user_id'>>;
export type AccountMemberUpdate = Partial<Pick<AccountMemberInput, 'role' | 'is_active' | 'accepted_at'>>;

export type PlaceType = 'room' | 'station' | 'stage' | 'venue' | string;
export type ThingType = 'equipment' | 'instrument' | 'furniture' | string;
export type ThingTrackingMode = 'lendable' | 'consumable';
export type ThingTransactionType = 'checkout' | 'return' | 'consume';
export type PersonType = 'artist' | 'guest' | 'staff' | 'support' | 'attendee' | string;
export type HappeningType = 'show' | 'event' | 'recording' | 'festival' | 'mix' | string;
export type TaskType = 'booking' | 'preparation' | 'follow_up' | 'technical' | string;

export type ThingStatus = 'available' | 'in_use' | 'maintenance' | 'retired';
export type HappeningStatus = 'draft' | 'scheduled' | 'confirmed' | 'completed' | 'cancelled';
export type TaskStatus = 'todo' | 'in_progress' | 'blocked' | 'done' | 'cancelled';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface Place {
  id: string;
  slug: string;
  name: string;
  type: PlaceType;
  parent_id: string | null;
  account_id: string | null;
  address: string | null;
  emergency_contact_phone: string | null;
  metadata: Record<string, unknown>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Thing {
  id: string;
  slug: string;
  /** Short human-typeable lookup code (e.g. "K7P2Q"). Unique per account, auto-generated, immutable. */
  code: string;
  name: string;
  type: ThingType;
  account_id: string | null;
  image_url: string | null;
  metadata: Record<string, unknown>;
  status: ThingStatus;
  tracking_mode: ThingTrackingMode;
  quantity: number;
  owner_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ThingTransaction {
  id: string;
  thing_id: string;
  person_id: string | null;
  /** Optional "where" the item went on a checkout. */
  place_id: string | null;
  account_id: string | null;
  type: ThingTransactionType;
  quantity: number;
  notes: string | null;
  created_at: string;
}

export type ThingTransactionCreate = Pick<ThingTransaction, 'thing_id' | 'person_id' | 'type' | 'quantity'> & {
  notes?: string | null;
  place_id?: string | null;
};

export interface ThingInventoryState {
  total_quantity: number;
  available: number;
  checked_out: number;
  consumed: number;
  active_checkouts: Array<{
    person_id: string;
    person_name: string;
    quantity: number;
    /** Distinct "where" the person took items, joined for display. */
    place_name?: string;
    /** Timestamp of the person's most recent checkout of this thing. */
    checked_out_at?: string;
  }>;
  recent_transactions: (ThingTransaction & { person_name?: string; place_name?: string })[];
}

export interface Person {
  id: string;
  slug: string;
  name: string;
  type: PersonType;
  account_id: string | null;
  email: string | null;
  phone: string | null;
  bio: string | null;
  social_links: Record<string, unknown>;
  image_url: string | null;
  metadata: Record<string, unknown>;
  profile_completion_token: string | null;
  profile_completed_at: string | null;
  profile_completion_deadline: string | null;
  is_public: boolean;
  is_collective: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Happening {
  id: string;
  slug: string;
  title: string;
  type: HappeningType;
  account_id: string | null;
  description: string | null;
  image_url: string | null;
  start_time: string | null;
  end_time: string | null;
  place_id: string | null;
  timezone: string | null; // IANA timezone identifier. If null, uses organization default timezone.
  is_public: boolean;
  status: HappeningStatus;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface HappeningParticipant {
  id: string;
  happening_id: string;
  person_id: string;
  role: string;
  sort_order: number;
  metadata: Record<string, unknown>;
  created_at: string;
}

export interface HappeningRequirement {
  id: string;
  happening_id: string;
  thing_id: string;
  quantity: number;
  notes: string | null;
  created_at: string;
}

// Place Equipment Types
export type PlaceEquipmentAvailabilityType = 'default' | 'on_request';

export interface PlaceEquipment {
  id: string;
  place_id: string;
  thing_id: string;
  account_id: string;
  availability_type: PlaceEquipmentAvailabilityType;
  default_quantity: number;
  notes: string | null;
  sort_order: number;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

// Extended Thing with place equipment context
export interface ThingWithPlaceContext extends Thing {
  place_equipment?: {
    availability_type: PlaceEquipmentAvailabilityType;
    default_quantity: number;
    notes: string | null;
  };
}

// Equipment availability summary for a place
export interface PlaceEquipmentSummary {
  place_id: string;
  place_name: string;
  default_equipment: ThingWithPlaceContext[];  // Always available at this place
  on_request_equipment: ThingWithPlaceContext[]; // Can be requested for this place
}

export interface Task {
  id: string;
  title: string;
  description: string | null;
  type: TaskType;
  status: TaskStatus;
  priority: TaskPriority;
  account_id: string | null;
  assigned_to_id: string | null;
  related_happening_id: string | null;
  related_person_id: string | null;
  related_place_id: string | null;
  related_thing_id: string | null;
  due_date: string | null;
  completed_at: string | null;
  reminder_enabled?: boolean;
  reminder_sent_at?: string | null;
  reminder_offset_hours?: number;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface TaskComment {
  id: string;
  task_id: string;
  author_id: string;
  content: string;
  created_at: string;
}

// Input types for creating/updating entities
// Note: account_id should be provided when creating entities for proper tenant isolation
export type PlaceInput = Omit<Place, 'id' | 'created_at' | 'updated_at'>;
export type PlaceCreate = Partial<PlaceInput> & Pick<PlaceInput, 'slug' | 'name' | 'type'>;
export type PlaceUpdate = Partial<Omit<PlaceInput, 'account_id'>>; // account_id cannot be changed

export type ThingInput = Omit<Thing, 'id' | 'created_at' | 'updated_at'>;
export type ThingCreate = Partial<ThingInput> & Pick<ThingInput, 'slug' | 'name' | 'type'>;
export type ThingUpdate = Partial<Omit<ThingInput, 'account_id' | 'code'>>; // account_id and code are immutable

export type PersonInput = Omit<Person, 'id' | 'created_at' | 'updated_at' | 'profile_completed_at'>;
export type PersonCreate = Partial<PersonInput> & Pick<PersonInput, 'slug' | 'name' | 'type'>;
export type PersonUpdate = Partial<Omit<PersonInput, 'account_id'>>;

export type HappeningInput = Omit<Happening, 'id' | 'created_at' | 'updated_at'>;
export type HappeningCreate = Partial<HappeningInput> & Pick<HappeningInput, 'slug' | 'title' | 'type'>;
export type HappeningUpdate = Partial<Omit<HappeningInput, 'account_id'>>;

export type HappeningParticipantCreate = Omit<HappeningParticipant, 'id' | 'created_at' | 'metadata' | 'sort_order'> & {
  metadata?: Record<string, unknown>;
  sort_order?: number;
};
export type HappeningRequirementCreate = Omit<HappeningRequirement, 'id' | 'created_at'>;

export type PlaceEquipmentInput = Omit<PlaceEquipment, 'id' | 'created_at' | 'updated_at'>;
export type PlaceEquipmentCreate = Partial<PlaceEquipmentInput> &
  Pick<PlaceEquipmentInput, 'place_id' | 'thing_id' | 'availability_type'>;
export type PlaceEquipmentUpdate = Partial<Omit<PlaceEquipmentInput, 'place_id' | 'thing_id' | 'account_id'>>;

export type TaskInput = Omit<Task, 'id' | 'created_at' | 'updated_at'>;
export type TaskCreate = Partial<TaskInput> & Pick<TaskInput, 'title' | 'type'>;
export type TaskUpdate = Partial<Omit<TaskInput, 'account_id'>> & {
  reminder_enabled?: boolean;
  reminder_offset_hours?: number;
  reminder_sent_at?: string | null;
};

export type TaskCommentCreate = Omit<TaskComment, 'id' | 'created_at'>;

export interface TaskReminder {
  id: string;
  task_id: string;
  offset_hours: number;
  sent_at: string | null;
  created_at: string;
  updated_at: string;
}

export type TaskReminderCreate = Omit<TaskReminder, 'id' | 'created_at' | 'updated_at' | 'sent_at'>;
export type TaskReminderUpdate = Partial<Pick<TaskReminder, 'offset_hours' | 'sent_at'>>;

export type CompletionReminderType = 'profile' | 'happening' | 'combined_artist_show' | 'residency_creation' | 'post_show';

export interface CompletionReminder {
  id: string;
  reminder_type: CompletionReminderType;
  entity_id: string; // person_id for profile/combined_artist_show, happening_id for happening
  recipient_email: string;
  recipient_name: string;
  offset_hours: number;
  happening_id: string | null; // Optional link to happening; required for combined_artist_show
  sent_at: string | null;
  // Set when a reminder failed in a way retrying cannot fix (e.g. the recipient
  // address is not a valid address). The column already existed; nothing wrote to
  // it, so a permanently undeliverable reminder retried every run forever.
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export type CompletionReminderCreate = Omit<CompletionReminder, 'id' | 'created_at' | 'updated_at' | 'sent_at' | 'error_message'>;
export type CompletionReminderUpdate = Partial<Pick<CompletionReminder, 'sent_at' | 'error_message'>>;

export interface HappeningReminder {
  id: string;
  happening_id: string;
  person_id: string;
  offset_hours: number;
  sent_at: string | null;
  created_at: string;
  updated_at: string;
}

export type HappeningReminderCreate = Omit<HappeningReminder, 'id' | 'created_at' | 'updated_at' | 'sent_at'>;
export type HappeningReminderUpdate = Partial<Pick<HappeningReminder, 'offset_hours' | 'sent_at'>>;

// API Keys
export interface ApiKey {
  id: string;
  account_id: string;
  created_by_user_id: string | null;
  name: string;
  key_hash: string; // Never expose this
  key_prefix: string; // First 12 chars for display (e.g., "lb_live_xxxx")
  last_used_at: string | null;
  expires_at: string | null;
  is_active: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ApiKeyCreate {
  name: string;
  expires_at?: string | null;
  metadata?: Record<string, unknown>;
}

export interface ApiKeyWithPlainText extends Omit<ApiKey, 'key_hash'> {
  key: string; // Plain text key (only returned once when created)
}

export interface ApiKeyValidationResult {
  account_id: string;
  account_slug: string;
  account_name: string;
  account_type: AccountType;
  key_id: string;
  key_name: string;
}

// Email template customization types
export type EmailType =
  | 'taskReminder'
  | 'profileCompletion'
  | 'happeningCompletion'
  | 'happeningStubLink'
  | 'happeningReminder'
  | 'completionNotification'
  | 'postShow';

export interface EmailTemplateOverride {
  subject?: string;           // Custom subject line with {{variables}}
  htmlBody?: string;          // Full HTML template override
  textBody?: string;          // Full plain text template override
  sections?: {                // Partial section overrides
    greeting?: string;
    intro?: string;
    callToAction?: string;
    footer?: string;
    [key: string]: string | undefined;
  };
  lastModified?: string;      // ISO timestamp
  modifiedBy?: string;        // User ID who last modified
}

export type VariableDataType = 'string' | 'date' | 'number' | 'url' | 'object' | 'html';

export interface TemplateVariable {
  name: string;              // Variable name: e.g., "toName"
  key: string;               // Mustache key: "{{toName}}"
  description: string;       // Human-readable description
  example: string;           // Example value for preview
  required: boolean;         // Is this variable required?
  dataType: VariableDataType; // Type of data
  category?: string;         // Grouping category (recipient, task, org, etc.)
}

export interface EmailTemplateSchema {
  type: EmailType;
  name: string;              // Display name: "Task Reminder"
  description: string;       // When this email is sent
  variables: TemplateVariable[];
  defaultSubject: string;    // Default subject line
  defaultHtml: string;       // Default HTML template
  defaultText: string;       // Default text template
  sampleData: Record<string, any>; // Sample data for preview
}

// Account settings structure with email templates
export interface AccountSettings {
  default_country_code?: string;
  preferred_image_ratio?: 'landscape' | 'portrait';
  recurrence_presets?: Array<{
    name: string;
    pattern: Record<string, any>;
  }>;
  emailBranding?: {
    logoUrl?: string;
    primaryColor?: string;
    secondaryColor?: string;
    footerText?: string;
    templates?: Record<string, {
      greeting?: string;
      intro?: string;
      footer?: string;
    }>;
  };
  webhook?: {
    url?: string;
    secret?: string;
  };
  emailTemplates?: Record<EmailType, EmailTemplateOverride>;
  postShowFeedbackUrl?: string; // Default feedback form URL for post-show emails
  websiteBaseUrl?: string; // Public website base URL for shows (e.g. https://radio.example.com). Used to build show page links in artist-facing emails such as the archive notification.
  postShowDefaultOffsetHours?: number; // Default delay in hours after show for post-show email (default: 24)
  defaultGenerateArtistLink?: boolean; // Default "generate artist-facing link" checkbox for new happenings
  defaultInviteEmailStrategy?: 'combined' | 'separate' | 'none'; // Default invite email strategy for new happenings
  [key: string]: unknown; // Allow other settings
}

// People Participants - staff assignments to roles for people (artists)
export interface PeopleParticipant {
  id: string;
  person_id: string;
  user_id: string | null;
  assigned_person_id: string | null;
  role: string;
  account_id: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
  // Joined fields for display
  user_name?: string;
  user_email?: string;
  assigned_person_name?: string;
}

export type PeopleParticipantCreate = {
  person_id: string;
  role: string;
  account_id: string;
  metadata?: Record<string, unknown>;
} & (
  | { user_id: string; assigned_person_id?: never }
  | { user_id?: never; assigned_person_id: string }
);

// Completion Notification Roles - defines which roles to notify on completion
export type CompletionNotificationEntityType = 'person' | 'happening';

export interface CompletionNotificationRole {
  id: string;
  entity_type: CompletionNotificationEntityType;
  entity_id: string;
  notify_role: string;
  account_id: string;
  created_at: string;
}

export type CompletionNotificationRoleCreate = Omit<CompletionNotificationRole, 'id' | 'created_at'>;

export type PendingNotificationType = 'completion' | 'technical_update' | 'archive_added';

export interface PendingCompletionNotification {
  id: string;
  entity_type: CompletionNotificationEntityType;
  entity_id: string;
  entity_name: string;
  completed_by_name: string;
  completed_at: string;
  account_id: string;
  notification_type: PendingNotificationType;
  change_description: string | null;
  sent_at: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export type PendingCompletionNotificationCreate = Omit<
  PendingCompletionNotification,
  'id' | 'sent_at' | 'error_message' | 'created_at' | 'updated_at'
>;

// Auth event types for audit logging
export type AuthEventType = 'login_success' | 'login_failed' | 'session_resumed';
export type AuthFailureReason = 'user_not_found' | 'invalid_password' | 'account_inactive' | 'user_inactive';

export interface AuthEvent {
  id: string;
  account_id: string | null;
  user_id: string | null;
  email: string;
  event_type: AuthEventType;
  failure_reason: AuthFailureReason | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

export interface AuthEventCreate {
  account_id?: string | null;
  user_id?: string | null;
  email: string;
  event_type: AuthEventType;
  failure_reason?: AuthFailureReason | null;
  ip_address?: string | null;
  user_agent?: string | null;
}

// Login result type for detailed auth responses
export type LoginResult =
  | { success: true; session: UserSession }
  | { success: false; reason: AuthFailureReason; userId?: string; accountId?: string };

// ============================================
// Programs (multi-stage groupings of happenings)
// ============================================

export interface Program {
  id: string;
  account_id: string;
  slug: string;
  name: string;
  description: string | null;
  start_time: string;
  end_time: string;
  settings: Record<string, unknown>;
  default_happening_type: string;
  created_at: string;
  updated_at: string;
}

export type ProgramInput = Omit<Program, 'id' | 'account_id' | 'created_at' | 'updated_at'>;
export type ProgramCreate = Partial<ProgramInput> & Pick<ProgramInput, 'slug' | 'name' | 'start_time' | 'end_time'>;
export type ProgramUpdate = Partial<Omit<ProgramInput, 'slug'>> & { slug?: string };

export interface ProgramPlace {
  program_id: string;
  place_id: string;
  sort_order: number;
  created_at: string;
}

export type ProgramPlaceCreate = {
  program_id: string;
  place_id: string;
  sort_order?: number;
};

// ============================================
// Staffing (availability-based staff assignment)
// ============================================

// Generic, account-scoped role catalog (stage manager, artist care, …).
export interface StaffRole {
  id: string;
  account_id: string;
  slug: string;
  name: string;
  created_at: string;
  updated_at: string;
}

// ============================================
// Tags
// ============================================

/**
 * Palette tokens the admin theme defines as --{token}-badge / --{token}-badge-foreground.
 * Kept in sync with the tags_color_check constraint in the database.
 */
export const TAG_COLORS = ['blue', 'orange', 'yellow', 'purple', 'green', 'red', 'amber'] as const;
export type TagColor = (typeof TAG_COLORS)[number];

// Generic, account-scoped label catalog applied to happenings.
export interface Tag {
  id: string;
  account_id: string;
  slug: string;
  label: string;
  color: TagColor;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export type TagInput = Omit<Tag, 'id' | 'account_id' | 'created_at' | 'updated_at'>;
export type TagCreate = Partial<TagInput> & Pick<TagInput, 'label'>;
export type TagUpdate = Partial<TagInput>;

// Which tags a happening carries.
export interface HappeningTag {
  happening_id: string;
  tag_id: string;
  created_at: string;
}

export type StaffRoleInput = Omit<StaffRole, 'id' | 'account_id' | 'created_at' | 'updated_at'>;
export type StaffRoleCreate = Partial<StaffRoleInput> & Pick<StaffRoleInput, 'name'>;
export type StaffRoleUpdate = Partial<StaffRoleInput>;

// Which roles a person is qualified for.
export interface PersonRole {
  person_id: string;
  role_id: string;
  created_at: string;
}

export interface StaffAvailability {
  id: string;
  account_id: string;
  person_id: string;
  program_id: string | null; // null = general availability
  start_time: string;
  end_time: string;
  created_at: string;
}

export type StaffAvailabilityCreate = {
  person_id: string;
  program_id?: string | null;
  start_time: string;
  end_time: string;
};

// Per-stage default requirement; prefill source for happenings on that stage.
export interface PlaceRoleDefault {
  place_id: string;
  role_id: string;
  count: number;
}

// Per-day override of a stage's base default requirement (day = YYYY-MM-DD).
export interface PlaceRoleDayDefault {
  place_id: string;
  role_id: string;
  day: string;
  count: number;
}

// Authoritative per-happening staffing need. count = 0 means explicitly not needed.
export interface HappeningRoleRequirement {
  id: string;
  happening_id: string;
  role_id: string;
  count: number;
  created_at: string;
}

export type HappeningRoleRequirementInput = {
  role_id: string;
  count: number;
};

export type StaffShiftSource = 'generated' | 'manual';

// A coverage slot. 'generated' shifts are derived from happening requirements
// and rebuilt by the engine; 'manual' shifts are user-created and preserved.
// A need of count=N yields N parallel generated shifts.
export interface StaffShift {
  id: string;
  account_id: string;
  program_id: string;
  place_id: string;
  role_id: string;
  start_time: string;
  end_time: string;
  source: StaffShiftSource;
  created_at: string;
  updated_at: string;
}

export type StaffAssignmentStatus = 'proposed' | 'confirmed';

export interface StaffAssignment {
  id: string;
  shift_id: string;
  person_id: string;
  status: StaffAssignmentStatus;
  is_locked: boolean; // a manual choice the engine must not overwrite
  created_at: string;
  updated_at: string;
}

// Per-person scheduling constraints, stored in people.metadata.staffing.
export interface StaffSchedulingConstraints {
  max_consecutive_hours?: number;
  max_daily_hours?: number;
  min_break_minutes?: number;
  allowed_place_ids?: string[];
}

// Shift joined with its (optional) assignment for timetable rendering.
export interface StaffShiftWithAssignment extends StaffShift {
  assignment: StaffAssignment | null;
  person_name?: string;
}

// ============================================
// Collective Selection (structured multi-round staff voting)
//
// A fixed roster of account users converges on a subset of happenings across
// ordered rounds. Ballots are temporally blind — invisible to everyone,
// including the owner, until a round is revealed (enforced in RLS). Voter
// identity is a `users` row; recorded-set audio is not modelled here (it lives
// in happenings.metadata.archive_links), but `Listen` tracks coverage.
// ============================================

export type SelectionState = 'draft' | 'active' | 'complete';
export type RoundKind = 'nominate' | 'allocate';
export type RoundState = 'draft' | 'open' | 'closed' | 'revealed';
export type BallotStatus = 'draft' | 'submitted';
export type ListenSource = 'manual' | 'player';

// Kind-specific round config, persisted as JSONB on selection_rounds.config.
export interface NominateRoundConfig {
  max_picks: number;
  wildcards: number;       // separate, smaller allowance; default 2
  require_note: boolean;
}

export interface AllocateRoundConfig {
  budget: number;          // points to distribute; default 100
  per_item_cap: number;    // max points on a single item; default 30
  require_note_above: number; // points threshold above which a note is required
}

export type RoundConfig = NominateRoundConfig | AllocateRoundConfig | Record<string, unknown>;

// How the owner turns nomination counts into a shortlist at reveal. Either a
// minimum nomination threshold, or the top-K by nomination count. Wildcards are
// unioned in afterwards regardless of the rule.
export type CutoffRule = { type: 'threshold'; min: number } | { type: 'top'; k: number };

// One row of an allocate-round tally. `score` is the sum of normalized shares
// (each voter's points/their-total), which is what ranks; `raw_points` is the
// human-legible sum of raw points.
export interface TallyRow {
  happening_id: string;
  raw_points: number;
  score: number;
  voter_count: number;
}

// A stored listHappenings() filter that resolves to the candidate happenings.
// Kept loose so it can carry whatever listHappenings supports over time.
export type SelectionScope = Record<string, unknown>;

export interface Selection {
  id: string;
  account_id: string;
  owner_id: string;        // users.id
  title: string;
  description: string | null;
  scope: SelectionScope;
  target_pct: number | null; // 0..1, advisory only
  state: SelectionState;
  settings: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export type SelectionInput = Omit<Selection, 'id' | 'account_id' | 'created_at' | 'updated_at'>;
export type SelectionCreate = Partial<SelectionInput> & Pick<SelectionInput, 'title' | 'owner_id'>;
export type SelectionUpdate = Partial<Omit<SelectionInput, 'owner_id'>> & { owner_id?: string };

export interface SelectionVoter {
  selection_id: string;
  user_id: string;
  created_at: string;
}

export interface SelectionRecusal {
  id: string;
  selection_id: string;
  user_id: string;
  happening_id: string;
  reason: string | null;
  created_at: string;
}

export type SelectionRecusalCreate = Omit<SelectionRecusal, 'id' | 'created_at'>;

export interface SelectionRound {
  id: string;
  selection_id: string;
  ordinal: number;
  kind: RoundKind;
  state: RoundState;
  opens_at: string | null;
  closes_at: string | null;
  config: RoundConfig;
  created_at: string;
  updated_at: string;
}

export type SelectionRoundInput = Omit<SelectionRound, 'id' | 'created_at' | 'updated_at'>;
export type SelectionRoundCreate = Partial<Omit<SelectionRoundInput, 'selection_id' | 'kind'>> &
  Pick<SelectionRoundInput, 'selection_id' | 'kind'>;
export type SelectionRoundUpdate = Partial<Pick<SelectionRound, 'state' | 'opens_at' | 'closes_at' | 'config'>>;

export interface SelectionBallot {
  id: string;
  round_id: string;
  voter_id: string;
  status: BallotStatus;
  submitted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SelectionBallotEntry {
  id: string;
  ballot_id: string;
  happening_id: string;
  points: number | null;   // null for nominate rounds
  is_wildcard: boolean;    // nominate rounds only
  note: string | null;
  created_at: string;
  updated_at: string;
}

// The shape a client submits for a single line; the ballot is addressed
// separately (by round + acting voter).
export type BallotEntryInput = {
  happening_id: string;
  points?: number | null;
  is_wildcard?: boolean;
  note?: string | null;
};

// A ballot plus its entries, the unit the ballot UI reads/writes.
export interface SelectionBallotWithEntries extends SelectionBallot {
  entries: SelectionBallotEntry[];
}

export interface SelectionIndividualPick {
  id: string;
  selection_id: string;
  voter_id: string;
  happening_id: string;
  justification: string | null;
  created_at: string;
  updated_at: string;
}

export type SelectionIndividualPickCreate = Omit<
  SelectionIndividualPick,
  'id' | 'created_at' | 'updated_at'
>;

export interface SelectionAuditEvent {
  id: string;
  account_id: string;
  selection_id: string;
  round_id: string | null;
  actor_user_id: string | null;
  action: string;
  scope: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
}

export type SelectionAuditEventCreate = Omit<SelectionAuditEvent, 'id' | 'created_at'>;

export interface Listen {
  id: string;
  account_id: string;
  user_id: string;
  happening_id: string;
  listened_at: string;
  position_seconds: number | null;
  source: ListenSource;
  created_at: string;
  updated_at: string;
}

// A listen is addressed by (happening, acting user); the writer supplies just
// the progress. account_id/user_id are stamped from context server-side.
export type ListenUpsert = {
  happening_id: string;
  position_seconds?: number | null;
  source?: ListenSource;
  listened_at?: string;
};

// One cell of the coverage grid: how many voters have heard a happening, and who.
export interface CoverageRow {
  happening_id: string;
  listener_user_ids: string[];
  listen_count: number;
  is_orphan: boolean;      // fewer than 2 listens
}
